import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { money, money2, timeAgo } from "@/lib/format";
import Pagination from "@/components/common/Pagination";
import {
  PageHeader,
  Loader,
  EmptyState,
  VegDot,
} from "@/components/common/Bits";
import { StatusBadge } from "@/components/common/StatusBadge";
import { cn } from "@/lib/utils";
import PaymentDialog from "@/components/common/PaymentDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ReceiptText,
  Eye,
  XCircle,
  Plus,
  CalendarDays,
  ChevronDown,
  Printer,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PRIMARY_TABS = ["all", "dine_in", "takeaway", "delivery"];

const STATUS_TABS = [
  "approved",
  "placed",
  "preparing",
  "ready",
  "completed",
  "cancelled",
  "unresolved",
];

const LABEL = {
  all: "All",
  dine_in: "Dine-In",
  takeaway: "Takeaway",
  delivery: "Delivery",
  placed: "Placed",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
  unresolved: "Unresolved",
  approved: "Approved",
};

export default function Orders() {
  const { restaurant, branches, branchId, subscribe } = useApp();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStarterPlan =
    String(user?.plan || user?.subscription?.plan || "").toLowerCase() ===
    "starter";
  const [tab, setTab] = useState("all");
  const [orders, setOrders] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });
  const [detail, setDetail] = useState(null);
  const [addItemOrder, setAddItemOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const receiptBranch =
    branches.find(
      (branch) => String(branch.id) === String(paymentOrder?.branch_id),
    ) ||
    branches.find((branch) => String(branch.id) === String(branchId)) ||
    branches[0];

  // Orders are shown for the selected business date.
  // The browser's local date is used so the default matches the
  // restaurant owner's local day instead of UTC.
  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getLocalDate);

  const load = (requestedPage = page) => {
    const params = {
      branch_id: branchId,
      page: requestedPage,
      page_size: pageSize,
    };

    // Normal tabs = selected date
    if (tab !== "unresolved") {
      params.date = selectedDate;
    }

    if (["dine_in", "takeaway", "delivery"].includes(tab)) {
      params.type = tab;
    } else if (tab === "unresolved") {
      params.unresolved = true;
    } else if (tab !== "all") {
      params.status = tab;
    }

    setOrders(null);

    api
      .orders(params)
      .then((r) => {
        setOrders(r.data.items || []);
        setPagination(
          r.data.pagination || {
            page: requestedPage,
            page_size: pageSize,
            total: 0,
            total_pages: 0,
            has_next: false,
            has_previous: false,
          },
        );
      })
      .catch(() => {
        setOrders([]);
        setPagination({
          page: 1,
          page_size: pageSize,
          total: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        });
      });
  };

  useEffect(() => {
    setPage(1);
    load(1);
  }, [branchId, tab, selectedDate]);

  useEffect(() => {
    const orderId = searchParams.get("orderId");

    if (!orderId) return;

    let cancelled = false;

    const openOrderFromUrl = async () => {
      try {
        const response = await api.order(orderId);

        if (!cancelled && response.data) {
          setDetail(response.data);

          // URL clean कर दो ताकि refresh/back पर
          // वही order बार-बार automatically open न हो.
          setSearchParams({}, { replace: true });
        }
      } catch {
        // Order current user/restaurant के लिए available
        // नहीं है तो normal Orders page ही रहेगा.
        if (!cancelled) {
          setSearchParams({}, { replace: true });
        }
      }
    };

    openOrderFromUrl();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const u1 = subscribe("order.new", () => load(page));
    const u2 = subscribe("order.update", () => load(page));
    return () => {
      u1();
      u2();
    };
  }, [subscribe, branchId, tab, selectedDate]);

  useEffect(() => {
    api
      .settings()
      .then((r) => setSettings(r.data.settings))
      .catch(() => {});
  }, [branchId]);

  const cancel = async (o) => {
    try {
      await api.cancelOrder(o.id);
      toast.success(`Order #${o.order_no} cancelled`);
      load();
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };
  const approve = async (o) => {
    try {
      await api.approveOrder(o.id);
      toast.success(`Order #${o.order_no} approved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openPayment = (o) => {
    setPaymentOrder({
      ...o,
      receiptOnly: o.payment_status === "paid",
    });
  };

  const genBill = async (o) => {
    try {
      await api.generateBill(o.id);
      toast.success(`Bill generated for #${o.order_no}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const detailPaymentMode =
    detail?.payments?.length === 1 ? detail.payments[0]?.mode : null;

  const detailPaymentDiscountPct =
    Number(settings?.payment_discounts?.[detailPaymentMode]) || 0;

  const detailPaymentDiscount =
    Math.round(
      ((Number(detail?.total || 0) * detailPaymentDiscountPct) / 100) * 100,
    ) / 100;

  const detailFinalAmount = Math.round(
    Number(detail?.total || 0) - detailPaymentDiscount,
  );

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Orders"
        subtitle="Track and manage all orders"
        actions={
          <button
            type="button"
            onClick={() => navigate("/app/pos")}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition"
            data-testid="orders-add-order"
          >
            <Plus size={16} />
            Add Order
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 mb-2 w-full">
        <div className="flex gap-2 min-w-0 w-full sm:w-auto overflow-x-auto pb-1">
          {/* Main filters */}
          {PRIMARY_TABS.filter((t) => !(isStarterPlan && t === "delivery")).map(
            (t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`orders-tab-${t}`}
                className={cn(
                  "shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                  tab === t
                    ? "bg-primary text-white"
                    : "bg-white border border-border text-foreground hover:bg-secondary",
                )}
              >
                {LABEL[t]}
              </button>
            ),
          )}

          {/* More status filters */}
          {!isStarterPlan && (
            <div className="relative shrink-0">
              <select
                value={STATUS_TABS.includes(tab) ? tab : ""}
                onChange={(e) => {
                  if (e.target.value) {
                    setTab(e.target.value);
                  }
                }}
                className={cn(
                  "h-9 w-[100px] appearance-none rounded-full pl-4 pr-4 text-sm font-semibold border outline-none cursor-pointer",
                  STATUS_TABS.includes(tab)
                    ? "bg-primary text-white border-primary"
                    : "bg-white border-border text-foreground",
                )}
              >
                <option value="" disabled>
                  More
                </option>

                {STATUS_TABS.map((status) => (
                  <option key={status} value={status}>
                    {LABEL[status]}
                  </option>
                ))}
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground"
              />
            </div>
          )}
        </div>

        {/* Date filter */}
        <div className="shrink-0 w-full sm:w-auto flex items-center gap-2 rounded-xl border border-border bg-white px-2.5 py-1.5 shadow-sm">
          <CalendarDays size={16} className="text-primary" />

          <input
            id="orders-date-filter"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="h-7 flex-1 sm:flex-none sm:w-[120px] min-w-0 bg-transparent text-xs font-semibold text-foreground outline-none cursor-pointer"
            data-testid="orders-date-filter"
            title="Filter orders by date"
          />

          <button
            type="button"
            onClick={() => setSelectedDate("")}
            className={cn(
              "h-7 rounded-lg px-2.5 text-xs font-semibold border transition",
              selectedDate
                ? "border-border bg-white text-foreground hover:bg-secondary"
                : "border-primary bg-primary text-white",
            )}
          >
            All
          </button>
        </div>
      </div>

      {!orders ? (
        <Loader />
      ) : orders.length === 0 ? (
        <EmptyState
          icon={ReceiptText}
          title="No orders yet"
          subtitle="Orders will appear here when they are placed from POS or QR ordering."
        />
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-muted-foreground">
                <tr className="text-center">
                  {[
                    "Order",
                    "Source",
                    "Type",
                    ...(isStarterPlan ? [] : ["Table/Customer"]),
                    "Amount",
                    ...(isStarterPlan ? [] : ["Approval"]),
                    "Payment",
                    ...(isStarterPlan ? [] : ["Status"]),
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className="border-t border-border hover:bg-secondary/30 text-center"
                    data-testid={`order-row-${o.id}`}
                  >
                    <td className="px-4 py-3 font-bold text-center">
                      <div className="flex flex-col items-center leading-tight">
                        <span>#{o.order_no}</span>
                        <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">
                          {o.created_at
                            ? new Date(o.created_at).toLocaleDateString(
                                undefined,
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "2-digit",
                                },
                              )
                            : "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {o.source ? <StatusBadge status={o.source} /> : "—"}
                    </td>
                    <td className="px-4 py-3 capitalize text-center">
                      {o.type.replace("_", " ")}
                    </td>
                    {!isStarterPlan && (
                      <td className="px-4 py-3 text-center">
                        {o.table_name || o.customer_name || "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 font-bold text-center">
                      {money2(
                        o.payment_status === "paid" ? o.paid_amount : o.total,
                      )}
                    </td>
                    {!isStarterPlan && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center">
                          <StatusBadge
                            status={o.approval_status || "not_required"}
                          />
                        </div>
                      </td>
                    )}
                    {/* {!isStarterPlan && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center">
                          <StatusBadge status={o.payment_status || "unpaid"} />
                        </div>
                      </td>
                    )} */}

                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <StatusBadge status={o.payment_status || "unpaid"} />
                      </div>
                    </td>

                    {!isStarterPlan && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center">
                          <StatusBadge status={o.status || "Ready"} />
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {o.approval_status === "pending" &&
                          o.source !== "qr" &&
                          user?.can_approve && (
                            <button
                              onClick={() => approve(o)}
                              className="h-8 px-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"
                              data-testid={`order-approve-${o.id}`}
                            >
                              Approve
                            </button>
                          )}
                        {o.status !== "completed" &&
                          o.status !== "cancelled" &&
                          o.approval_status === "approved" &&
                          o.payment_status !== "paid" && (
                            <>
                              <button
                                onClick={() => {
                                  navigate("/app/pos", {
                                    state: {
                                      existingOrder: o,
                                      addItemMode: true,
                                    },
                                  });
                                }}
                                className="h-8 px-2 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100"
                                data-testid={`order-add-item-${o.id}`}
                              >
                                Add Item
                              </button>

                              <button
                                onClick={() => openPayment(o)}
                                className="h-8 px-2 rounded-lg bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100"
                                data-testid={`order-bill-${o.id}`}
                              >
                                Bill
                              </button>
                            </>
                          )}
                        <button
                          onClick={() => setDetail(o)}
                          className="h-8 w-8 rounded-lg hover:bg-secondary grid place-items-center"
                          data-testid={`order-view-${o.id}`}
                        >
                          <Eye size={15} />
                        </button>
                        {["placed", "preparing"].includes(o.status) && (
                          <button
                            onClick={() => cancel(o)}
                            className="h-8 w-8 rounded-lg hover:bg-red-50 text-red-600 grid place-items-center"
                            data-testid={`order-cancel-${o.id}`}
                          >
                            <XCircle size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={pagination.page}
            pageSize={pagination.page_size}
            total={pagination.total}
            totalPages={pagination.total_pages}
            onPageChange={(nextPage) => {
              setPage(nextPage);
              load(nextPage);
            }}
          />
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent
          className="bg-white sm:max-w-md"
          data-testid="order-detail"
        >
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center justify-between">
                  Order #{detail.order_no}
                  <StatusBadge status={detail.payment_status || "unpaid"} />
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm space-y-1 text-muted-foreground">
                <p className="capitalize">
                  {detail.type.replace("_", " ")} ·{" "}
                  {detail.table_name || detail.customer_name || "Walk-in"} ·{" "}
                  {timeAgo(detail.created_at)} ago
                </p>
              </div>
              <div className="border rounded-xl divide-y">
                {detail.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2">
                    <VegDot veg={it.veg} />
                    <span className="flex-1 text-sm font-medium">
                      {it.name}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ×{it.qty}
                    </span>
                    <span className="text-sm font-semibold w-16 text-right">
                      {money2(it.price * it.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm">
                <Row l="Subtotal" v={money2(detail.subtotal)} />

                {detail.discount_total > 0 && (
                  <Row
                    l="Bill Discount"
                    v={`- ${money2(detail.discount_total)}`}
                  />
                )}

                <Row l="GST" v={money2(detail.tax_amount)} />

                <div className="flex justify-between pt-1 border-t text-sm">
                  <span>Gross Total</span>
                  <span className="font-semibold">{money2(detail.total)}</span>
                </div>

                {detailPaymentDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>
                      {detailPaymentMode} Discount ({detailPaymentDiscountPct}%)
                    </span>
                    <span className="text-green-600 font-semibold">
                      - {money2(detailPaymentDiscount)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between pt-1 border-t font-bold font-display text-base">
                  <span>Final Amount</span>
                  <span className="text-primary">
                    {money(detailFinalAmount)}
                  </span>
                </div>
              </div>
              {detail.payments?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Paid via{" "}
                  {detail.payments
                    .map((p) => `${p.mode} ${money2(p.amount)}`)
                    .join(", ")}
                </div>
              )}

              <div className="pt-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => openPayment(detail)}
                >
                  <Printer size={16} />
                  Reprint & Share Receipt
                  <Share2 size={16} />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <PaymentDialog
        open={!!paymentOrder}
        setOpen={(open) => {
          if (!open) setPaymentOrder(null);
        }}
        total={paymentOrder?.total || 0}
        existingOrder={paymentOrder}
        receiptOnly={paymentOrder?.receiptOnly || false}
        onDone={() => {
          setPaymentOrder(null);
          load();
        }}
        settings={settings}
        restaurantName={restaurant?.name}
        restaurantAddress={receiptBranch?.address || ""}
        restaurantPhone={receiptBranch?.phone || ""}
        restaurantTagline={restaurant?.tagline || ""}
      />
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between text-muted-foreground">
    <span>{l}</span>
    <span className="font-semibold text-foreground">{v}</span>
  </div>
);
